import { ArchiveIcon, PencilLineIcon, UsersIcon } from "lucide-react";

import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";

import type { DashboardArtifactSummary, DashboardArtifactVersionSummary } from "./artifactTypes";

export type ArtifactBannerState = "draft" | "published" | "archived";

/** A version is a draft until it is the published one, even on a published artifact. */
export function artifactBannerState(
  artifact: Pick<DashboardArtifactSummary, "status">,
  version: Pick<DashboardArtifactVersionSummary, "published">,
): ArtifactBannerState {
  if (artifact.status === "archived") return "archived";
  return version.published ? "published" : "draft";
}

/**
 * Whether this version is a draft or shared with the team. Whether a number
 * can be trusted is shown per number, in its trust popover.
 */
export function ArtifactStatusBanner(props: {
  artifact: DashboardArtifactSummary;
  version: DashboardArtifactVersionSummary;
}) {
  const state = artifactBannerState(props.artifact, props.version);
  const versionLabel = `Version ${props.version.version} by ${props.version.createdByEmail}`;

  if (state === "archived") {
    return (
      <Alert data-artifact-status="archived">
        <ArchiveIcon aria-hidden />
        <AlertTitle>Archived</AlertTitle>
        <AlertDescription>
          {versionLabel}. Archived dashboards no longer load data.
        </AlertDescription>
      </Alert>
    );
  }
  if (state === "draft") {
    return (
      <Alert variant="warning" data-artifact-status="draft">
        <PencilLineIcon aria-hidden />
        <AlertTitle>Draft</AlertTitle>
        <AlertDescription>{versionLabel}. Not shared with the team yet.</AlertDescription>
      </Alert>
    );
  }
  return (
    <Alert variant="success" data-artifact-status="published">
      <UsersIcon aria-hidden />
      <AlertTitle>Shared with the team</AlertTitle>
      <AlertDescription>{versionLabel}.</AlertDescription>
    </Alert>
  );
}
